import { Button as BaseButton } from "@base-ui/react/button";
import walletIcon from "@loot-os/icons/icons/wallet";
import type { ComponentProps } from "react";
import { Icon } from "../core/Icon.js";
import { useCountTo } from "../core/useCountTo.js";
import { useRiseCount } from "../core/useRiseCount.js";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING, PRESS } from "../foundations/interaction.js";
import { Amount } from "./Amount.js";
import { useCurrency } from "./currencyContext.js";

export interface BalancePillProps
  extends Omit<ComponentProps<typeof BaseButton>, "className" | "children"> {
  /** The spendable balance in cents. */
  cents: number;
  /** The button's name before the amount, e.g. "Balance". */
  label?: string;
  className?: string;
}

/**
 * The shopper's spendable balance in the chrome; pressing it opens Add funds. A currency with an
 * icon of its own draws only that icon and the number; one written with a symbol ("$") leads with
 * the wallet, so the chip still reads as money beside the store's other controls. A rise rolls up
 * to the new balance (a spend shows at once); `data-slot="balance-pill"` is where Deposit's trail
 * (`balanceTrail.ts`) lands, on the pill's first mark.
 */
export function BalancePill({ cents, label = "Balance", className, ...props }: BalancePillProps) {
  // The wallet plays when the balance rises (funds added, a sale), never when it is spent.
  const rises = useRiseCount(cents);
  const hasOwnIcon = useCurrency().iconSvg !== "";
  const shown = useCountTo(cents, undefined, undefined, "rise");
  return (
    <BaseButton
      data-slot="balance-pill"
      className={cn(
        "inline-flex h-control-sm shrink-0 cursor-pointer items-center gap-2 rounded-control border border-border-default bg-bg-raised px-3 text-text-primary hover:bg-bg-hover",
        PRESS,
        FOCUS_RING,
        className,
      )}
      {...props}
    >
      {!hasOwnIcon && <Icon icon={walletIcon} className="text-text-secondary" playOn={rises} />}
      <span className="sr-only">{label}</span>
      <Amount cents={shown} />
    </BaseButton>
  );
}
